/**
 * Button — terminal-style primary button + variants.
 *
 * Variants:
 *   primary  — fire fill, ink text (CTA)
 *   secondary— bordered, neutral
 *   ghost    — no background, hover elevates
 *   danger   — rose
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-fire text-ink hover:brightness-110 border border-fire ' +
    'disabled:opacity-60 disabled:cursor-not-allowed',
  secondary:
    'bg-elevated border border-line text-text hover:border-lineHi ' +
    'disabled:opacity-50',
  ghost:
    'text-textDim hover:text-text hover:bg-elevated ' +
    'border border-transparent',
  danger:
    'bg-rose text-ink hover:brightness-110 border border-rose ' +
    'disabled:opacity-60',
}

const sizeClasses: Record<Size, string> = {
  sm: 'h-7  px-2.5 text-xs',
  md: 'h-9  px-4   text-sm',
  lg: 'h-11 px-5   text-base',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary', size = 'md',
      loading = false, disabled, className, children, ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center gap-2',
          'font-medium tracking-tight rounded',
          'transition-all duration-100 select-none',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...rest}
      >
        {loading && (
          <span
            className="inline-block w-3 h-3 border-2 border-current border-t-transparent
                       rounded-full animate-spin"
            aria-hidden
          />
        )}
        {children}
      </button>
    )
  },
)
